import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Zap, MouseOff, CornerUpLeft, AlertCircle } from 'lucide-react';
import { useInsightsSummary } from '@/hooks/use-insights';
import { InsightCard } from '@/components/insights/insight-card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type Range = '24h' | '7d' | '30d';

export const Route = createFileRoute('/_authenticated/sites/$siteId/insights')({
  component: InsightsPage,
});

function InsightsPage() {
  const { siteId } = Route.useParams();
  const [range, setRange] = useState<Range>('7d');
  const { data, isLoading } = useInsightsSummary(siteId, range);

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-up">
      <header className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="mb-1 text-2xl font-semibold tracking-tight">Insights</h1>
          <p className="text-sm text-text-muted">Detected UX issues across user sessions.</p>
        </div>
        <div className="inline-flex rounded-md border border-border bg-bg-surface p-0.5 gap-0.5">
          {(['24h', '7d', '30d'] as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={cn(
                'px-3 py-1 text-xs font-medium rounded transition-colors',
                range === r
                  ? 'bg-bg-surface-3 text-text shadow-[0_0_0_1px_var(--border-strong)]'
                  : 'text-text-muted hover:text-text',
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </header>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InsightCard icon={Zap} tone="rage" title="Rage clicks" description="3+ clicks in 1s on the same element" count={data?.rage_click.count ?? 0} />
          <InsightCard icon={MouseOff} tone="dead" title="Dead clicks" description="No DOM mutation within 500ms" count={data?.dead_click.count ?? 0} />
          <InsightCard icon={CornerUpLeft} tone="quick" title="Quick backs" description="Sessions bouncing back to entry within 5s" count={data?.quick_back.count ?? 0} />
          <InsightCard icon={AlertCircle} tone="error" title="JavaScript errors" description="Uncaught exceptions captured" count={data?.error.count ?? 0} />
        </div>
      )}
    </div>
  );
}
